import { motion } from "framer-motion";

// --------- Animated concentric contour lines with elevation labels ---------
export function ContourLines({ className = "", opacity = 0.2, centerColor = "#06b6d4", count = 8 }) {
    const elevations = [1100, 1125, 1150, 1175, 1200, 1225, 1250, 1275];
    return (
        <svg className={className} viewBox="0 0 600 600" style={{ opacity }}>
            <defs>
                <radialGradient id="contourFade" cx="50%" cy="50%">
                    <stop offset="0%" stopColor={centerColor} stopOpacity="0.6" />
                    <stop offset="80%" stopColor={centerColor} stopOpacity="0.1" />
                    <stop offset="100%" stopColor={centerColor} stopOpacity="0" />
                </radialGradient>
            </defs>
            {Array.from({ length: count }).map((_, i) => {
                const r = 40 + i * 32;
                return (
                    <motion.g
                        key={`contour-${r}`}
                        initial={{ opacity: 0, scale: 0.4 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.12, duration: 1.2, ease: "easeOut" }}
                    >
                        <circle
                            cx="300" cy="300" r={r}
                            fill="none"
                            stroke={i % 3 === 0 ? "#22d3ee" : "#0891b2"}
                            strokeWidth={i % 3 === 0 ? "0.8" : "0.4"}
                            strokeDasharray={i % 3 === 0 ? "none" : "3 2"}
                        />
                        {elevations[i] && (
                            <text
                                x={300 + r * Math.cos(Math.PI / 4)}
                                y={300 + r * Math.sin(Math.PI / 4)}
                                fill="#67e8f9"
                                fontSize="9"
                                fontFamily="JetBrains Mono, monospace"
                                opacity="0.7"
                            >
                                EL {elevations[i]}
                            </text>
                        )}
                    </motion.g>
                );
            })}
            <circle cx="300" cy="300" r="6" fill={centerColor} />
            <circle cx="300" cy="300" r="12" fill="none" stroke={centerColor} strokeWidth="1" opacity="0.6" />
        </svg>
    );
}

// --------- Survey point markers with field abbreviations ---------
const SURVEY_POINTS = [
    { type: "IP", label: "IP", sym: "●", color: "#22d3ee" },
    { type: "PK", label: "PK", sym: "⊕", color: "#fb923c" },
    { type: "MON", label: "MON", sym: "▲", color: "#a78bfa" },
    { type: "BM", label: "BM EL 1248.75", sym: "◆", color: "#34d399" },
    { type: "CP", label: "CP-1", sym: "⊙", color: "#22d3ee" },
    { type: "TBM", label: "TBM 1251.2", sym: "■", color: "#fbbf24" },
];

export function SurveyPoint({ x, y, type = "IP", label, size = "sm", delay = 0 }) {
    const p = SURVEY_POINTS.find((s) => s.type === type) || SURVEY_POINTS[0];
    const text = label ?? p.label;
    const fontSize = size === "sm" ? 9 : 11;
    return (
        <motion.g
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay, duration: 0.4, type: "spring", stiffness: 180 }}
        >
            <circle cx={x} cy={y} r="3" fill={p.color} />
            <circle cx={x} cy={y} r="8" fill="none" stroke={p.color} strokeWidth="0.6" opacity="0.4" />
            <line x1={x - 12} y1={y} x2={x - 4} y2={y} stroke={p.color} strokeWidth="0.5" opacity="0.6" />
            <line x1={x + 4} y1={y} x2={x + 12} y2={y} stroke={p.color} strokeWidth="0.5" opacity="0.6" />
            <line x1={x} y1={y - 12} x2={x} y2={y - 4} stroke={p.color} strokeWidth="0.5" opacity="0.6" />
            <line x1={x} y1={y + 4} x2={x} y2={y + 12} stroke={p.color} strokeWidth="0.5" opacity="0.6" />
            <text x={x + 10} y={y - 6} fill={p.color} fontSize={fontSize} fontFamily="JetBrains Mono, monospace" fontWeight="600">
                {text}
            </text>
        </motion.g>
    );
}

// --------- Property boundary with bearing/distance labels ---------
export function PropertyBoundary({ className = "", opacity = 0.35 }) {
    return (
        <svg className={className} viewBox="0 0 800 500" style={{ opacity }}>
            <defs>
                <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M0,0 L10,5 L0,10 z" fill="#22d3ee" />
                </marker>
            </defs>
            {/* Property polygon */}
            <motion.polygon
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 2.5, ease: "easeInOut" }}
                points="100,100 700,120 720,400 80,420"
                fill="none"
                stroke="#22d3ee"
                strokeWidth="1.2"
                strokeDasharray="8 4"
            />
            {/* Bearing / distance labels */}
            <text x="380" y="90" fill="#67e8f9" fontSize="11" fontFamily="JetBrains Mono, monospace" textAnchor="middle">
                N 89°45'12" E &nbsp;&nbsp; 600.32'
            </text>
            <text x="740" y="260" fill="#67e8f9" fontSize="11" fontFamily="JetBrains Mono, monospace" transform="rotate(88 740 260)" textAnchor="middle">
                S 02°15'40" E &nbsp;&nbsp; 280.15'
            </text>
            <text x="400" y="445" fill="#67e8f9" fontSize="11" fontFamily="JetBrains Mono, monospace" textAnchor="middle">
                S 89°50'30" W &nbsp;&nbsp; 640.18'
            </text>
            <text x="60" y="260" fill="#67e8f9" fontSize="11" fontFamily="JetBrains Mono, monospace" transform="rotate(-88 60 260)" textAnchor="middle">
                N 02°30'15" W &nbsp;&nbsp; 320.42'
            </text>
            {/* Corner monuments */}
            {[
                { x: 100, y: 100, label: "POB" },
                { x: 700, y: 120, label: "MON #1" },
                { x: 720, y: 400, label: "MON #2" },
                { x: 80, y: 420, label: "MON #3" },
            ].map((c) => (
                <g key={c.label}>
                    <circle cx={c.x} cy={c.y} r="4" fill="#fb923c" />
                    <circle cx={c.x} cy={c.y} r="9" fill="none" stroke="#fb923c" strokeWidth="0.8" />
                    <text x={c.x + 12} y={c.y - 8} fill="#fb923c" fontSize="10" fontFamily="JetBrains Mono, monospace" fontWeight="600">
                        {c.label}
                    </text>
                </g>
            ))}
            <text x="400" y="260" fill="#a78bfa" fontSize="14" fontFamily="Outfit, sans-serif" fontWeight="600" textAnchor="middle" opacity="0.7">
                LOT 7 — 4.25 AC
            </text>
        </svg>
    );
}

// --------- Subdivision grid with lot numbers ---------
export function SubdivisionGrid({ className = "", opacity = 0.2 }) {
    const lots = [
        { x: 40, y: 40, w: 80, h: 60, n: "1" },
        { x: 130, y: 40, w: 80, h: 60, n: "2" },
        { x: 220, y: 40, w: 80, h: 60, n: "3" },
        { x: 310, y: 40, w: 80, h: 60, n: "4" },
        { x: 40, y: 110, w: 80, h: 70, n: "5" },
        { x: 130, y: 110, w: 80, h: 70, n: "6" },
        { x: 220, y: 110, w: 80, h: 70, n: "7" },
        { x: 310, y: 110, w: 80, h: 70, n: "8" },
        { x: 40, y: 190, w: 160, h: 60, n: "9" },
        { x: 210, y: 190, w: 180, h: 60, n: "10" },
    ];
    return (
        <svg className={className} viewBox="0 0 450 280" style={{ opacity }}>
            {lots.map((l) => (
                <g key={l.n}>
                    <rect x={l.x} y={l.y} width={l.w} height={l.h} fill="none" stroke="#a78bfa" strokeWidth="0.6" />
                    <text x={l.x + l.w / 2} y={l.y + l.h / 2 + 3} fill="#c4b5fd" fontSize="10" fontFamily="JetBrains Mono, monospace" textAnchor="middle" fontWeight="600">
                        L-{l.n}
                    </text>
                </g>
            ))}
            {/* Road annotations */}
            <text x="220" y="100" fill="#fbbf24" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle" opacity="0.8">
                MAPLE ST. · 50' R/W
            </text>
            <text x="220" y="190" fill="#fbbf24" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle" opacity="0.8">
                OAK DR. · 50' R/W
            </text>
        </svg>
    );
}

// --------- GIS-style coordinate grid with station markers ---------
export function CoordinateGrid({ className = "", opacity = 0.15 }) {
    return (
        <svg className={className} viewBox="0 0 600 400" style={{ opacity }}>
            <defs>
                <pattern id="minor" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(34,211,238,0.25)" strokeWidth="0.3" />
                </pattern>
                <pattern id="major" width="100" height="100" patternUnits="userSpaceOnUse">
                    <path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(34,211,238,0.5)" strokeWidth="0.6" />
                </pattern>
            </defs>
            <rect width="600" height="400" fill="url(#minor)" />
            <rect width="600" height="400" fill="url(#major)" />
            {/* Coordinate labels (N/E in US State Plane format) */}
            {[0, 100, 200, 300, 400, 500, 600].map((x) => (
                <text key={`x${x}`} x={x + 2} y="12" fill="#22d3ee" fontSize="7" fontFamily="JetBrains Mono, monospace" opacity="0.7">
                    E 1,{String(500000 + x * 10).slice(0, 3)},{String(500000 + x * 10).slice(3)}
                </text>
            ))}
            {[0, 100, 200, 300, 400].map((y) => (
                <text key={`y${y}`} x="4" y={y + 20} fill="#22d3ee" fontSize="7" fontFamily="JetBrains Mono, monospace" opacity="0.7">
                    N 842,{String(500 + y).slice(0, 3)}
                </text>
            ))}
        </svg>
    );
}

// --------- Animated compass rose ---------
export function CompassRose({ className = "", size = 120 }) {
    return (
        <motion.svg
            className={className}
            width={size} height={size} viewBox="0 0 120 120"
            animate={{ rotate: [0, 2, -2, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        >
            <circle cx="60" cy="60" r="55" fill="none" stroke="#22d3ee" strokeWidth="0.8" opacity="0.5" />
            <circle cx="60" cy="60" r="48" fill="none" stroke="#0891b2" strokeWidth="0.4" strokeDasharray="2 2" opacity="0.6" />
            <circle cx="60" cy="60" r="36" fill="none" stroke="#0891b2" strokeWidth="0.4" opacity="0.4" />
            {/* Compass points */}
            {[0, 90, 180, 270].map((deg, i) => (
                <g key={deg} transform={`rotate(${deg} 60 60)`}>
                    <polygon points="60,8 56,30 60,26 64,30" fill={i === 0 ? "#fb923c" : "#22d3ee"} opacity={i === 0 ? 1 : 0.7} />
                </g>
            ))}
            {[45, 135, 225, 315].map((deg) => (
                <g key={deg} transform={`rotate(${deg} 60 60)`}>
                    <polygon points="60,14 58,28 60,26 62,28" fill="#0891b2" opacity="0.5" />
                </g>
            ))}
            <text x="60" y="22" fill="#fb923c" fontSize="11" fontFamily="Outfit, sans-serif" fontWeight="800" textAnchor="middle">N</text>
            <text x="60" y="104" fill="#22d3ee" fontSize="9" fontFamily="Outfit, sans-serif" fontWeight="600" textAnchor="middle">S</text>
            <text x="102" y="64" fill="#22d3ee" fontSize="9" fontFamily="Outfit, sans-serif" fontWeight="600" textAnchor="middle">E</text>
            <text x="18" y="64" fill="#22d3ee" fontSize="9" fontFamily="Outfit, sans-serif" fontWeight="600" textAnchor="middle">W</text>
            <circle cx="60" cy="60" r="3" fill="#f97316" />
        </motion.svg>
    );
}

// --------- Benchmark monument (detailed decorative) ---------
export function Monument({ className = "", size = 60 }) {
    return (
        <svg className={className} width={size} height={size * 1.4} viewBox="0 0 60 84">
            <motion.g
                initial={{ y: -6, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <polygon points="30,4 46,30 14,30" fill="none" stroke="#fb923c" strokeWidth="1.2" />
                <rect x="14" y="30" width="32" height="36" fill="none" stroke="#fb923c" strokeWidth="1.2" />
                <line x1="22" y1="40" x2="38" y2="40" stroke="#fb923c" strokeWidth="0.6" />
                <text x="30" y="52" fill="#fb923c" fontSize="7" fontFamily="JetBrains Mono, monospace" textAnchor="middle" fontWeight="600">S2C</text>
                <text x="30" y="60" fill="#fb923c" fontSize="6" fontFamily="JetBrains Mono, monospace" textAnchor="middle" opacity="0.8">MON</text>
                <circle cx="30" cy="66" r="2" fill="#fb923c" />
                <line x1="10" y1="72" x2="50" y2="72" stroke="#fb923c" strokeWidth="1.5" />
                <line x1="5" y1="78" x2="55" y2="78" stroke="#fb923c" strokeWidth="0.8" strokeDasharray="2 2" />
            </motion.g>
        </svg>
    );
}

// --------- Full surveying scene (combines multiple elements) ---------
export function SurveyingScene({ className = "", opacity = 0.35 }) {
    return (
        <div className={className} style={{ opacity }}>
            <svg viewBox="0 0 800 500" className="w-full h-full">
                <defs>
                    <pattern id="sceneGrid" width="30" height="30" patternUnits="userSpaceOnUse">
                        <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(34,211,238,0.12)" strokeWidth="0.4" />
                    </pattern>
                </defs>
                <rect width="800" height="500" fill="url(#sceneGrid)" />

                {/* Property boundary polygon */}
                <motion.polygon
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 3, ease: "easeInOut" }}
                    points="120,90 640,110 660,400 100,420"
                    fill="rgba(6,182,212,0.04)"
                    stroke="#22d3ee"
                    strokeWidth="1.3"
                    strokeDasharray="6 4"
                />

                {/* Contour rings */}
                {[80, 120, 160, 200].map((r, i) => (
                    <circle key={r} cx="400" cy="250" r={r} fill="none" stroke="#a78bfa" strokeWidth="0.5" strokeDasharray="3 3" opacity={0.3 - i * 0.05} />
                ))}
                <text x="570" y="180" fill="#c4b5fd" fontSize="10" fontFamily="JetBrains Mono, monospace" opacity="0.7">EL 1225.50'</text>
                <text x="570" y="310" fill="#c4b5fd" fontSize="10" fontFamily="JetBrains Mono, monospace" opacity="0.7">EL 1275.25'</text>

                {/* Survey points */}
                <SurveyPoint x="120" y="90" type="PK" label="POB · PK-1" delay={0.8} />
                <SurveyPoint x="640" y="110" type="IP" label="IP-2" delay={1.0} />
                <SurveyPoint x="660" y="400" type="MON" label="MON-3" delay={1.2} />
                <SurveyPoint x="100" y="420" type="IP" label="IP-4" delay={1.4} />
                <SurveyPoint x="400" y="250" type="BM" label="BM EL 1248.75" delay={1.6} />
                <SurveyPoint x="280" y="180" type="CP" label="CP-1" delay={1.8} />
                <SurveyPoint x="520" y="340" type="TBM" label="TBM 1251.20" delay={2.0} />

                {/* Bearing/distance labels */}
                <text x="380" y="80" fill="#67e8f9" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle" opacity="0.85">
                    N 89°45'12" E · 520.32'
                </text>
                <text x="370" y="445" fill="#67e8f9" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle" opacity="0.85">
                    S 89°50'30" W · 560.18'
                </text>

                {/* Scale & north arrow */}
                <g transform="translate(700, 440)">
                    <line x1="0" y1="0" x2="60" y2="0" stroke="#22d3ee" strokeWidth="1" />
                    <line x1="0" y1="-3" x2="0" y2="3" stroke="#22d3ee" strokeWidth="1" />
                    <line x1="30" y1="-2" x2="30" y2="2" stroke="#22d3ee" strokeWidth="0.6" />
                    <line x1="60" y1="-3" x2="60" y2="3" stroke="#22d3ee" strokeWidth="1" />
                    <text x="30" y="15" fill="#22d3ee" fontSize="8" fontFamily="JetBrains Mono, monospace" textAnchor="middle">0    30    60'</text>
                </g>
            </svg>
        </div>
    );
}

// --------- Floating animated field annotations for hero backgrounds ---------
export function FloatingAnnotations({ className = "" }) {
    const items = [
        { text: "EL 1248.75", x: "8%", y: "18%", delay: 0.5, color: "#22d3ee" },
        { text: "N 45°30' E", x: "18%", y: "72%", delay: 1.0, color: "#a78bfa" },
        { text: "BM #4", x: "78%", y: "28%", delay: 1.5, color: "#fb923c" },
        { text: "IP · 0.5\" REBAR", x: "85%", y: "76%", delay: 2.0, color: "#34d399" },
        { text: "LOT 7 · 4.25 AC", x: "62%", y: "12%", delay: 2.5, color: "#fbbf24" },
        { text: "42°15'40\" N", x: "12%", y: "42%", delay: 3.0, color: "#67e8f9" },
        { text: "STA 15+75", x: "70%", y: "58%", delay: 3.5, color: "#c4b5fd" },
    ];
    return (
        <div className={`absolute inset-0 pointer-events-none ${className}`}>
            {items.map((it) => (
                <motion.div
                    key={it.text}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: [0, 0.85, 0.85, 0], y: [10, 0, 0, -10] }}
                    transition={{ duration: 6, delay: it.delay, repeat: Infinity, repeatDelay: 3 }}
                    className="absolute font-mono-alt text-[10px] tracking-wider px-2 py-1 rounded border backdrop-blur-sm"
                    style={{
                        left: it.x, top: it.y,
                        color: it.color,
                        borderColor: `${it.color}40`,
                        background: `${it.color}10`,
                    }}
                >
                    {it.text}
                </motion.div>
            ))}
        </div>
    );
}
